import React, { useEffect, useMemo, useState } from 'react';

import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import HierarchicalSearchablePopover from '~/components/core/Molecules/HierarchicalSearchablePopover/HierarchicalSearchablePopover';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { buildFieldTree } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/buildFieldTree';

import { CONFIGURATIONS_TEXTS as TEXTS } from './texts';
import type { ClaimFieldCatalogFetch } from './useClaimFieldCatalog';
import useClaimFieldCatalog from './useClaimFieldCatalog';

type AddFieldButtonProps = {
  organizationId: number;
  lob: string | null;
  subOrganizationId: number | null;
  /** Keys the configuration already resolves or holds as added; the server refuses them, so they are not offered. */
  resolvedKeys: ReadonlySet<string>;
  /** Holds the picked field on the screen; it is saved with the rest of the scope's changes. */
  onAdd: (field: { field_key: string; display_name: string }) => void;
  disabled?: boolean;
  /** The scope's catalog, already read by the pane. Without it the button reads the catalog when it opens. */
  catalog?: ClaimFieldCatalogFetch;
};

const AddFieldButton = ({
  organizationId,
  lob,
  subOrganizationId,
  resolvedKeys,
  onAdd,
  disabled = false,
  catalog: sharedCatalog,
}: AddFieldButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isNothingToAdd, setIsNothingToAdd] = useState(false);
  const isOpen = anchorEl !== null;

  // A shared read that failed is read again here when the menu opens, as the button does without one.
  const isSharedUsable = sharedCatalog !== undefined && !sharedCatalog.isError;
  const ownCatalog = useClaimFieldCatalog(organizationId, lob, subOrganizationId, isOpen && !isSharedUsable);
  const { data: catalog, isLoading } = isSharedUsable ? sharedCatalog : ownCatalog;

  const addable = useMemo(
    () => (catalog?.fields ?? []).filter((entry) => !resolvedKeys.has(entry.field_key)),
    [catalog, resolvedKeys]
  );
  const options = useMemo(
    () =>
      buildFieldTree(
        addable.map((entry) => ({
          name: entry.field_key,
          label: entry.label,
          group: entry.group,
        }))
      ),
    [addable]
  );

  // The popover opens only with something to pick, so it cannot close itself when there is nothing: close it
  // here and say so instead, or it would open unasked once the configuration resolves fewer fields.
  useEffect(() => {
    if (isOpen && !isLoading && catalog && addable.length === 0) {
      setAnchorEl(null);
      setIsNothingToAdd(true);
    }
  }, [isOpen, isLoading, catalog, addable.length]);
  useEffect(() => {
    setIsNothingToAdd(false);
  }, [resolvedKeys]);

  const handleSelect = (fieldKey: string) => {
    const entry = addable.find((candidate) => candidate.field_key === fieldKey);
    setAnchorEl(null);
    if (entry) {
      onAdd({ field_key: entry.field_key, display_name: entry.label });
    }
  };

  return (
    <div className="flex items-center gap-8">
      {isNothingToAdd ? (
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.noCatalogFields}
        </Text>
      ) : null}
      <Button
        variant="text"
        color="primary"
        className="outline-none focus-visible:ring-2 focus-visible:ring-teal-700 [&.Mui-focusVisible_.MuiTouchRipple-ripplePulsate]:hidden"
        disabled={disabled}
        onClick={(event) => {
          setIsNothingToAdd(false);
          setAnchorEl(event.currentTarget);
        }}
      >
        <AddIcon />
        {TEXTS.addField}
      </Button>
      <HierarchicalSearchablePopover
        open={isOpen && addable.length > 0}
        anchorEl={anchorEl}
        label={TEXTS.addField}
        options={options}
        onClose={() => setAnchorEl(null)}
        onSelect={(token) => handleSelect(token.id)}
      />
    </div>
  );
};

export default AddFieldButton;
